/**
 * Magic Link Exchange Page
 *
 * Handles the redirect from email magic links (legacy flow).
 * Exchanges the token for auth tokens and redirects to home.
 *
 * Note: New users should use the code-based flow (LoginPage → email → code).
 * This page is kept for backward compatibility with old magic links.
 */

import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { saveTokens, setRememberedEmail } from "../../lib/storage.ts";
import { useExchangeMagicLink } from "@/features/auth/hooks";
import { useSafeTimeout } from "../../lib/hooks/use-safe-timeout.ts";
import { getErrorMessage } from "../../lib/graphql.ts";

export function MagicLinkPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { setTimeout: safeSetTimeout } = useSafeTimeout();
  
  const { exchangeMagicLink } = useExchangeMagicLink();
  
  // Track if we've already started the exchange to prevent double execution
  const hasStartedRef = useRef(false);

  useEffect(() => {
    const token = searchParams.get("token");
    // Cancellation flag for cleanup
    let cancelled = false;

    if (!token) {
      setStatus("error");
      setErrorMessage("Ссылка недействительна");
      return;
    }
    
    // Prevent double execution (React strict mode)
    if (hasStartedRef.current) return;
    hasStartedRef.current = true;

    const exchangeToken = async () => {
      try {
        const result = await exchangeMagicLink(token);
        
        // Guard against state updates after unmount
        if (cancelled) return;

        await saveTokens(result.tokens);
        const accountEmail = result.account.email;
        if (accountEmail?.trim()) setRememberedEmail(accountEmail.trim());
        
        if (cancelled) return;
        setStatus("success");

        // Redirect to home after a short delay (safe timeout handles cleanup)
        safeSetTimeout(() => navigate("/", { replace: true }), 1500);
      } catch (err) {
        // Guard against state updates after unmount
        if (cancelled) return;
        
        setStatus("error");
        if (err instanceof Error) {
          if (err.message.includes("expired")) {
            setErrorMessage("Срок действия ссылки истёк. Запросите новую.");
          } else if (err.message.includes("already been used")) {
            setErrorMessage("Эта ссылка уже была использована.");
          } else {
            setErrorMessage(getErrorMessage(err, "Ошибка авторизации"));
          }
        } else {
          setErrorMessage("Ошибка авторизации");
        }
      }
    };

    exchangeToken();
    
    return () => {
      cancelled = true;
    };
  }, [searchParams, navigate, exchangeMagicLink, safeSetTimeout]);

  return (
    <div className="flex-1 flex flex-col items-center justify-center p-4 page-container relative z-20 safe-area-top safe-area-bottom">
      <div className="card p-6 w-full max-w-sm text-center">
        {status === "loading" && (
          <>
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-blue-100 flex items-center justify-center">
              <div className="w-8 h-8 border-3 border-blue-200 border-t-blue-500 rounded-full animate-spin" />
            </div>
            <h2 className="text-lg font-semibold text-slate-900 mb-2">
              Авторизация
            </h2>
            <p className="text-sm text-slate-500">
              Подождите, проверяем ссылку...
            </p>
          </>
        )}

        {status === "success" && (
          <>
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
              <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-slate-900 mb-2">
              Добро пожаловать!
            </h2>
            <p className="text-sm text-slate-500">
              Перенаправляем на главную...
            </p>
          </>
        )}

        {status === "error" && (
          <>
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
              <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-slate-900 mb-2">
              Ошибка
            </h2>
            <p className="text-sm text-red-600 mb-4">
              {errorMessage}
            </p>
            <button
              type="button"
              onClick={() => navigate("/", { replace: true })}
              className="px-4 py-2 text-sm font-medium text-white rounded-xl transition-all"
              style={{
                background: "linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)",
              }}
            >
              На главную
            </button>
          </>
        )}
      </div>
    </div>
  );
}
